import { DEFAULT_CURRENCY_FORMATTER } from "@redotech/money/util";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Divider } from "@redotech/redo-web/arbiter-components/divider/divider";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useState } from "react";
import type { BisFilterProps } from "./filter-props";
import { NotifyModal } from "./notify-modal";
import { ProductDetailHeader, StatItem } from "./product-detail-header";
import { SubscribersList } from "./subscribers-list";

export interface VariantInfo {
  productId: string;
  variantId: string;
  name: string;
  sku: string;
  imageUrl: string | undefined;
  waitingCount: number;
  notifiedCount: number;
  price: number;
}

export const VariantDetail = memo(function VariantDetail({
  variant,
  subscriberSearch,
  setSubscriberSearch,
  refetchAllProducts,
  ...filterProps
}: {
  variant: VariantInfo;
  subscriberSearch: string;
  setSubscriberSearch: (q: string) => void;
  refetchAllProducts: () => void;
} & BisFilterProps) {
  const [isNotifyModalOpen, setIsNotifyModalOpen] = useState(false);

  return (
    <Flex dir="column" flex="1" gap="none" overflow="hidden">
      <ProductDetailHeader
        imageUrl={variant.imageUrl}
        name={variant.name}
        stats={
          <Flex align="center" gap="3xl">
            <StatItem label="waiting" value={variant.waitingCount.toString()} />
            <StatItem
              label="notified"
              value={variant.notifiedCount.toString()}
            />
            <StatItem
              label="price"
              value={DEFAULT_CURRENCY_FORMATTER.format(variant.price)}
            />
          </Flex>
        }
        subtitle={`SKU: ${variant.sku}`}
      />

      <Divider />

      <Flex align="center" px="xl" py="md">
        <Text fontSize="md" fontWeight="semibold" textColor="primary">
          Subscribers
        </Text>
      </Flex>

      <SubscribersList
        {...filterProps}
        footer={(subscribers) => {
          const totalWaiting = subscribers.filter(
            (s) => s.status === "waiting",
          ).length;
          return (
            <>
              <RedoButton
                disabled={totalWaiting === 0}
                hierarchy="primary"
                onClick={() => setIsNotifyModalOpen(true)}
                size="sm"
                text="Notify all waiting"
              />
              <NotifyModal
                isOpen={isNotifyModalOpen}
                onClose={() => setIsNotifyModalOpen(false)}
                onSuccess={refetchAllProducts}
                productId={variant.productId}
                totalWaiting={totalWaiting}
                variantId={variant.variantId}
              />
            </>
          );
        }}
        productId={variant.productId}
        setSubscriberSearch={setSubscriberSearch}
        showVariantColumn={false}
        subscriberSearch={subscriberSearch}
        variantId={variant.variantId}
      />
    </Flex>
  );
});
